import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  syncBetaFeatures,
  syncClinicBetaFeatures,
  syncClinicBetaFeaturesLoaded,
  syncUserEmail,
} from "../../util/betaFeatures";

export interface IAuthStateSlice {
  isAuthorized?: boolean;
  isPinAuthorized?: boolean;
  authState?: string;
  error?: string;
  user?: User;
  loginState?: string;
  challenge?: string;
  userPreference?: UserPreference;
  pinTries: number;
  pinLockTime: number | null;
  hasNoAccess: boolean | null;
  smsData?: {
    phoneNumber: string;
    isVerified: boolean;
  };
  betaFeaturesAllowedList?: string[];
  /** Clinic-scoped beta features for the selected clinic (target_type
   *  "clinic"). Populated once a clinic is selected; used with
   *  `betaFeaturesAllowedList` to gate features on user∩clinic. */
  clinicBetaFeaturesAllowedList?: string[];
  /** True once this session's clinic beta features have resolved (or there is
   *  no clinic to load them for). Lets the landing redirect wait for the
   *  2026 UI decision instead of guessing from a half-loaded state. */
  clinicBetaFeaturesLoaded?: boolean;
  betaFeaturesLoaded?: boolean;
}

const name = "Auth";

export const initialState: IAuthStateSlice = {
  isAuthorized: false,
  isPinAuthorized: false,
  authState: "pending",
  error: "",
  user: undefined,
  loginState: "login",
  userPreference: undefined,
  pinTries: 0,
  pinLockTime: null,
  hasNoAccess: null,
  smsData: undefined,
  betaFeaturesAllowedList: [],
  clinicBetaFeaturesAllowedList: [],
  clinicBetaFeaturesLoaded: false,
  betaFeaturesLoaded: false,
};

export const authSlice = createSlice({
  name,
  initialState,
  reducers: {
    setAuthState: (state, action: PayloadAction<Partial<IAuthStateSlice>>) => {
      const next = { ...state, ...action.payload };
      if ("betaFeaturesAllowedList" in action.payload) {
        syncBetaFeatures(action.payload.betaFeaturesAllowedList);
      } else if ("user" in action.payload && !action.payload.user) {
        syncBetaFeatures(null);
      }
      if ("clinicBetaFeaturesAllowedList" in action.payload) {
        syncClinicBetaFeatures(action.payload.clinicBetaFeaturesAllowedList);
      } else if ("user" in action.payload && !action.payload.user) {
        syncClinicBetaFeatures(null);
      }
      // Keep the dependency-free "clinic context resolved" cache in sync so the
      // internal 2026-UI launch override (usesEffectiveCip20Ui) waits for clinic
      // resolution, matching the reactive hook. Covers the no-clinic case; a
      // selected clinic is tracked separately via syncSelectedClinicMode.
      if ("clinicBetaFeaturesLoaded" in action.payload) {
        syncClinicBetaFeaturesLoaded(action.payload.clinicBetaFeaturesLoaded);
      } else if ("user" in action.payload && !action.payload.user) {
        // Clearing the user (logout/account-switch) must also reset the Redux
        // field, not just the module cache: `next` spreads the old state, so
        // without this the flag rehydrates `true` into the next session and
        // `useUse2026UI` could treat it as resolved before populateAppData runs.
        next.clinicBetaFeaturesLoaded = false;
        syncClinicBetaFeaturesLoaded(false);
      }
      // Keep the dependency-free email cache in sync for the internal 2026-UI
      // launch override (usesEffectiveCip20Ui).
      if ("user" in action.payload) {
        syncUserEmail(next.user?.email ?? null);
      }
      return next;
    },
    setPinTries: (state, action: PayloadAction<number>) => {
      state.pinTries = action.payload;
    },
    setPinLockTime: (state, action: PayloadAction<number | null>) => {
      state.pinLockTime = action.payload;
    },
    setUser: (state, action: PayloadAction<Partial<User>>) => {
      state.user = { ...state.user, ...action.payload } as User;
      syncUserEmail(state.user?.email ?? null);
    },
  },
});

export const { setAuthState, setPinTries, setPinLockTime, setUser } =
  authSlice.actions;

export default authSlice.reducer;
